import { useState } from 'react'

import { type DefaultContext, type TypedDocumentNode, useApolloClient } from '@apollo/client'
import {
  DataCollectionPaginatedFetchOptions,
  DataCollectionSource,
  DataCollectionSourceDefinition,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  NavigationFiltersState,
  PaginatedResponse,
  RecordType,
  SortingsDefinition,
  SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'

import { DEFAULT_PAGE_SIZE, MapVariablesParams, PaginationVars } from './useGraphqlDataSource'

export type GraphqlDataCollectionMappedVariables<Document = TypedDocumentNode> =
  | Omit<VariablesOf<Document>, 'page' | 'perPage' | 'cursor'>
  | undefined

export type GraphqlDataCollectionSourceDefinition<
  R extends RecordType = RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Variables extends PaginationVars = PaginationVars,
  Document extends TypedDocumentNode<ResultOf<Document>, Variables> = TypedDocumentNode,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<R> = GroupingDefinition<R>,
  Summaries extends SummariesDefinition = SummariesDefinition,
> = Omit<
  DataCollectionSourceDefinition<
    R,
    Filters,
    Sortings,
    Summaries,
    ItemActionsDefinition<R>,
    Navigation,
    Grouping
  >,
  'dataAdapter'
> & {
  document: Document
  fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only'
  paginationType?: 'pages' | 'infinite-scroll'
  perPage?: number
  queryContext?: DefaultContext
  includePaginationVars?: boolean
  mapVariables: ({
    filters,
    sortings,
    search,
    navigationFilters,
    pagination,
  }: MapVariablesParams<Filters> & {
    navigationFilters?: NavigationFiltersState<Navigation>
  }) => Omit<VariablesOf<Document>, 'page' | 'perPage' | 'cursor'>
  mapData: (data: ResultOf<Document>) => Array<R>
  mapSummaries?: (data: ResultOf<Document>) => R | undefined
  mapPagination: (data: ResultOf<Document>) => {
    total: number
    cursor?: string
    hasNextPage?: boolean
  }
}

export type GraphqlDataCollectionSource<
  R extends RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Summaries extends SummariesDefinition = SummariesDefinition,
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>> = TypedDocumentNode,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<R> = GroupingDefinition<R>,
> = DataCollectionSource<
  R,
  Filters,
  Sortings,
  Summaries,
  ItemActionsDefinition<R>,
  Navigation,
  Grouping
> & {
  mappedVariables: Omit<VariablesOf<Document>, 'page' | 'perPage' | 'cursor'> | undefined
  document: Document
}

export const useGraphqlDataCollectionSource = <
  Record extends RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Summaries extends SummariesDefinition = SummariesDefinition,
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>> = TypedDocumentNode,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<Record> = GroupingDefinition<Record>,
>(
  {
    mapVariables,
    mapData,
    mapPagination,
    mapSummaries,
    document,
    fetchPolicy = 'cache-and-network',
    paginationType = 'pages',
    perPage = DEFAULT_PAGE_SIZE,
    includePaginationVars = true,
    queryContext,
    ...rest
  }: GraphqlDataCollectionSourceDefinition<
    Record,
    Filters,
    Sortings,
    Extract<VariablesOf<Document>, PaginationVars>,
    Document,
    Navigation,
    Grouping,
    Summaries
  >,
  deps: ReadonlyArray<unknown> = []
): GraphqlDataCollectionSource<
  Record,
  Filters,
  Sortings,
  Summaries,
  Document,
  Navigation,
  Grouping
> => {
  const client = useApolloClient()

  const [mappedVariables, setMappedVariables] =
    useState<GraphqlDataCollectionMappedVariables<Document>>()

  const source = useDataCollectionSource<
    Record,
    Filters,
    Sortings,
    Summaries,
    ItemActionsDefinition<Record>,
    Navigation,
    Grouping
  >(
    {
      ...rest,
      dataAdapter: {
        perPage,
        paginationType,
        fetchData: (options: DataCollectionPaginatedFetchOptions<Filters, Navigation>) => {
          const { filters, sortings, search, navigationFilters } = options

          const pagination =
            'pagination' in options
              ? options.pagination
              : {
                  perPage: DEFAULT_PAGE_SIZE,
                  currentPage: 1,
                }

          const variables = mapVariables({
            filters,
            sortings,
            search,
            navigationFilters,
            pagination,
          })
          setMappedVariables(variables)

          const paginationVars: PaginationVars = {
            perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
            __notExcludePaginationKeysFromKeyArgs: true,
            ...(paginationType === 'pages'
              ? { page: pagination?.currentPage || 1, __paginationKind: 'offset' as const }
              : {
                  ...(pagination?.cursor && pagination.cursor !== '0'
                    ? {
                        cursor: pagination.cursor,
                        after: pagination.cursor,
                        __paginationKind: 'cursor' as const,
                        __paginationInfiniteScroll: true,
                      }
                    : {
                        __paginationKind: 'cursor' as const,
                        __paginationInfiniteScroll: true,
                      }),
                }),
          }

          let finalFetchPolicy = fetchPolicy
          if (
            paginationType === 'infinite-scroll' &&
            pagination.cursor &&
            pagination.cursor !== '0'
          ) {
            finalFetchPolicy = 'network-only'
          }

          const observable = client
            .watchQuery({
              query: document,
              fetchPolicy: finalFetchPolicy,
              variables: includePaginationVars
                ? {
                    ...variables,
                    ...paginationVars,
                  }
                : variables,
              context: queryContext,
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              const mappedData = mapData(data)
              const mappedPagination = mapPagination(data)
              const mappedSummaries = mapSummaries?.(data)

              const baseData = {
                records: mappedData,
                total: mappedPagination.total,
                perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
              }

              const fromCache = loading && !!data

              const paginatedData: PaginatedResponse<Record> =
                paginationType === 'pages'
                  ? {
                      ...baseData,
                      type: 'pages',
                      currentPage: pagination?.currentPage ?? 1,
                      pagesCount: Math.ceil(mappedPagination.total / paginationVars.perPage),
                    }
                  : {
                      ...baseData,
                      records: fromCache ? mappedData.slice(0, paginationVars.perPage) : mappedData,
                      type: 'infinite-scroll',
                      cursor: mappedPagination.cursor ?? null,
                      hasMore: Boolean(mappedPagination.hasNextPage),
                    }

              return {
                loading,
                error,
                data: {
                  ...paginatedData,
                  summaries: mappedSummaries,
                },
              }
            })

          return observable
        },
      },
    },
    deps
  )

  return { ...source, mappedVariables, document }
}
